import { AbstractComponent } from "appworks/components/abstract-component";
import { Components } from "appworks/components/components";
import { particleManager } from "appworks/graphics/particles/particle-manager";
import { DualPosition } from "appworks/graphics/pixi/dual-position";
import { Text } from "appworks/graphics/pixi/text";
import { gameState } from "appworks/model/game-state";
import { Services } from "appworks/services/services";
import { Contract } from "appworks/utils/contracts/contract";
import { Point } from "appworks/utils/geom/point";
import { RandomRangeInt } from "appworks/utils/math/random";
import { Timer } from "appworks/utils/timer";
import { Easing, Interpolation, Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { TGRInstantWin, TGRInstantWinResult } from "integration/builders/results/tgr-instant-win-result";
import { Emitter } from "pixi-particles";
import { MatrixComponent } from "slotworks/components/matrix/matrix-component";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { TGRSoundEvent } from "tgr-sound-event-enum";
import { TGRSymbolComponent } from "./tgr-symbol";

const EASING: (k: number) => number = Easing.Quadratic.Out;
const COUNTUP_DURATION: number = 2200;

export class TGRInstantWinsComponent extends AbstractComponent {

    public animateCollect(instantWinResult: TGRInstantWinResult, instantWin: TGRInstantWin, duration: number): Contract<void> {
        return new Contract<void>((resolve: (value?: any) => void) => {
            const symbol: TGRSymbolComponent = Components.get(MatrixComponent).getSymbol(instantWin.gridPosition.x, instantWin.gridPosition.y) as TGRSymbolComponent;
            const cltPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_${instantWinResult.collectSymbolPosition.x}_${instantWinResult.collectSymbolPosition.y}`);
            const rgtPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_rgt_${RandomRangeInt(0, 2)}`);
            const midPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_mid`);
            const symPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_${instantWin.gridPosition.x}_${instantWin.gridPosition.y}`);

            const emitter: Emitter = particleManager.get(particleManager.add("star_particles", gameLayers.InstantWins.container));
            emitter.updateSpawnPos(cltPos.landscape.x + (cltPos.landscape.width / 2), cltPos.landscape.y + (cltPos.landscape.height / 2));

            const tweenPath: { x: number[], y: number[] } = {
                x: [
                    rgtPos.landscape.x + (rgtPos.landscape.width / 2),
                    midPos.landscape.x + (midPos.landscape.width / 2),
                    symPos.landscape.x + (symPos.landscape.width / 2)
                ],
                y: [
                    rgtPos.landscape.y + (rgtPos.landscape.height / 2),
                    midPos.landscape.y + (midPos.landscape.height / 2),
                    symPos.landscape.y + (symPos.landscape.height / 2)
                ]
            };
            const tweenObj: {x: number, y: number} =  {
                x: cltPos.landscape.x + (cltPos.landscape.width / 2),
                y: cltPos.landscape.y + (cltPos.landscape.height / 2)
            };

            const tween: Tween = new Tween(tweenObj);
            tween.to(tweenPath, duration);
            tween.onStart(() => {
                Services.sound.customEvent(TGRSoundEvent.monkey_fizz);
                emitter.emit = true;
            });
            tween.interpolation(Interpolation.Bezier);
            tween.easing(EASING);
            tween.onUpdate(() => emitter.updateSpawnPos(tweenObj.x, tweenObj.y));
            tween.onComplete(() => {
                symbol.setSymbol("QH");
                (gameState.getCurrentGame().getCurrentRecord() as SpinRecord).grid[instantWin.gridPosition.x][instantWin.gridPosition.y] = "QH";
                emitter.emit = false;
                resolve();
            });
            tween.start();
        });
    }

    public animateWin(instantWin: TGRInstantWin, duration: number): Contract<void> {
        return new Contract<void>((resolve: (value?: any) => void) => {

            const gridPosition: Point = instantWin.gridPosition;
            const symPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_${gridPosition.x}_${gridPosition.y}`);
            const midPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_mid`);
            const rgtPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_rgt_${RandomRangeInt(0, 2)}`);
            const topPos: DualPosition = gameLayers.InstantWins.getPosition(`pos_top`);

            if (symPos && midPos && topPos) {

                const x: number = symPos.landscape.x + (symPos.landscape.width / 2);
                const y: number = symPos.landscape.y + (symPos.landscape.height / 2);

                const starEmitter: Emitter = particleManager.get(particleManager.add("star_particles", gameLayers.InstantWins.container));
                starEmitter.updateSpawnPos(x, y);

                const leafEmitter: Emitter = particleManager.get(particleManager.add("leaf_particles", gameLayers.InstantWins.container));
                leafEmitter.updateSpawnPos(x, y);

                const leafFallEmitter: Emitter = particleManager.get(particleManager.add("leaf_fall", gameLayers.InstantWins.container));
                leafFallEmitter.updateSpawnPos(x, y);

                const tweenPath: { x: number[], y: number[] } = {
                    x: [
                        midPos.landscape.x + (midPos.landscape.width / 2),
                        rgtPos.landscape.x + (rgtPos.landscape.width / 2),
                        topPos.landscape.x + (topPos.landscape.width / 2)
                    ],
                    y: [
                        midPos.landscape.y + (midPos.landscape.height / 2),
                        rgtPos.landscape.y + (rgtPos.landscape.height / 2),
                        topPos.landscape.y + (topPos.landscape.height / 2)
                    ]
                };
                const tweenObj: {x: number, y: number} =  { x, y };
                const tween = new Tween(tweenObj);
                tween.to(tweenPath, duration);
                tween.onStart(() => {
                    Services.sound.customEvent(TGRSoundEvent.monkey_collect);
                    starEmitter.emit = true;
                    leafEmitter.emit = true;
                    leafFallEmitter.emit = true;
                    Timer.setTimeout(() => leafFallEmitter.emit = false, 300);
                });
                tween.interpolation(Interpolation.Bezier);
                tween.easing(EASING);
                tween.onUpdate(() => {
                    starEmitter.updateSpawnPos(tweenObj.x, tweenObj.y);
                    leafEmitter.updateSpawnPos(tweenObj.x, tweenObj.y);
                });
                tween.onComplete(() => {
                    starEmitter.emit = false;
                    leafEmitter.emit = false;
                    leafFallEmitter.destroy();
                    resolve();
                });
                tween.start();
            }
        });
    }

    public incrementWinTo(winValue: number, delay: number): Contract<void> {
        return new Contract<void>((resolve: (value?: any) => void) => {
            const textElem: Text = gameLayers.InstantWins.getText("value");
            const valueObj: { value: number } = { value: 0 };
            const tickupTween = new Tween(valueObj);
            tickupTween.to({ value: winValue }, COUNTUP_DURATION);
            tickupTween.delay(delay);
            tickupTween.onStart(() => Services.sound.customEvent(TGRSoundEvent.countup_quickhit_start));
            tickupTween.easing(Easing.Sinusoidal.Out);
            tickupTween.onUpdate(() => textElem.text = Services.currency.format(valueObj.value));
            tickupTween.onComplete(() => {
                Services.sound.customEvent(TGRSoundEvent.countup_quickhit_end);
                textElem.text = Services.currency.format(winValue);
                resolve();
            });
            tickupTween.start();
        });

    }
}

export const tgrInstantWinsComponent = new TGRInstantWinsComponent();
